/**
 * Mükellef Sorgulama — Risk Kontrolü ekranının sekmesi.
 *
 * TCKN/VKN ile GİB e-Fatura mükellef listesini sorgular (Kolaysoft getGibUser).
 * Kolaysoft'taki "TC gir, e-Arşiv mi e-Fatura mı söylesin" sorgusunun karşılığı.
 * Fatura oluşturmadan, müşteri kartı açmadan bakabilmek için buraya kondu.
 */

import React, { useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { checkInvoiceRecipient } from '../../utils/invoiceEdmApi';
import { iv } from './invoiceTheme';
import { riskTableWrapSx } from './riskControlShared';

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const SatirBilgi = ({ label, value }) =>
  value ? (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Typography sx={{ fontSize: 12.5, color: iv.textMuted, fontWeight: 700, minWidth: 92 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: iv.text, wordBreak: 'break-all' }}>{value}</Typography>
    </Stack>
  ) : null;

export default function InvoiceTaxpayerLookupPanel({ sube, invoiceSettings }) {
  const [taxId, setTaxId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [gecmis, setGecmis] = useState([]);

  const digits = onlyDigits(taxId);
  const gecerli = digits.length === 10 || digits.length === 11;
  const testMode = invoiceSettings?.testMode !== false;

  const sorgula = async () => {
    if (!gecerli) {
      setError('10 haneli VKN veya 11 haneli TCKN girin.');
      setResult(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await checkInvoiceRecipient({ sube, taxId: digits });
      setResult(r);
      setGecmis((prev) => [
        { id: `${digits}-${Date.now()}`, identifier: digits, isEfatura: Boolean(r.isEfatura), title: r.title || '' },
        ...prev.filter((x) => x.identifier !== digits)
      ].slice(0, 8));
    } catch (err) {
      setResult(null);
      setError(err?.message || 'Sorgulanamadı');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ ...riskTableWrapSx, p: 2.25, maxWidth: 560, width: '100%', alignSelf: 'flex-start', overflow: 'auto' }}>
      <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.4 }}>TCKN / VKN ile mükellef sorgusu</Typography>
      <Typography sx={{ fontSize: 13, color: iv.textMuted, mb: 1.75 }}>
        Müşterinin GİB e-Fatura mükellefi olup olmadığını gösterir. Mükellefse fatura <b>e-Fatura</b>, değilse{' '}
        <b>e-Arşiv</b> olarak kesilir. Fatura oluşturmadan da bakabilirsiniz.
      </Typography>

      <Stack spacing={1.25}>
        <TextField
          size="small"
          label="TCKN veya VKN"
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !busy) sorgula();
          }}
          fullWidth
          helperText={digits && !gecerli ? `${digits.length} hane girildi — 10 veya 11 hane olmalı` : ' '}
          error={Boolean(digits) && !gecerli}
        />
        <Button
          variant="contained"
          disabled={busy || !gecerli}
          onClick={sorgula}
          sx={{ textTransform: 'none', fontWeight: 800, alignSelf: 'flex-start', minHeight: 40, px: 2 }}
        >
          {busy ? 'Sorgulanıyor…' : 'Sorgula'}
        </Button>

        {error ? (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: iv.danger || '#b91c1c' }}>{error}</Typography>
        ) : null}

        {result ? (
          <Box sx={{ pt: 0.5, border: `1px solid ${iv.border}`, borderRadius: 1.5, p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                color={result.isEfatura ? 'success' : 'default'}
                label={result.isEfatura ? 'e-Fatura mükellefi' : 'Mükellef değil → e-Arşiv'}
                sx={{ fontWeight: 800 }}
              />
              {testMode ? (
                <Chip size="small" color="warning" variant="outlined" label="TEST ortamı" sx={{ fontWeight: 700 }} />
              ) : null}
            </Stack>
            <Stack spacing={0.5}>
              <SatirBilgi label="Kimlik" value={result.identifier} />
              <SatirBilgi label="Ünvan" value={result.title} />
              <SatirBilgi label="GİB etiketi" value={result.alias} />
              <SatirBilgi label="Birim" value={result.unit} />
            </Stack>
          </Box>
        ) : null}

        {gecmis.length ? (
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, mb: 0.6 }}>
              BU OTURUMDA SORGULANANLAR
            </Typography>
            <Stack spacing={0.4}>
              {gecmis.map((g) => (
                <Stack key={g.id} direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    color={g.isEfatura ? 'success' : 'default'}
                    label={g.isEfatura ? 'e-Fatura' : 'e-Arşiv'}
                    sx={{ fontWeight: 700, height: 20, fontSize: 11 }}
                  />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{g.identifier}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: iv.textMuted, minWidth: 0, noWrap: true }}>
                    {g.title}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>

      {testMode ? (
        <Typography sx={{ fontSize: 12, color: '#b45309', fontWeight: 700, mt: 1.75, lineHeight: 1.55 }}>
          Test ortamında sorgu gerçek GİB mükellef listesini döndürmeyebilir. Canlı hesap bağlandığında sonuçlar
          gerçek olacaktır.
        </Typography>
      ) : null}
    </Box>
  );
}
